/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import type { NormalizedSavedView } from "@/types/saved-views";

/**
 * Platform presets are standard saved views available to all users.
 * These are read-only views that appear first in the saved views dropdown.
 */
export function getPlatformPresets<TFilters = Record<string, unknown>>(
  viewType: "clients" | "projects" | "estimates" | "contacts",
): NormalizedSavedView<TFilters>[] {
  const basePreset: Omit<
    NormalizedSavedView<TFilters>,
    "id" | "created_at" | "updated_at" | "filters"
  > = {
    name: "",
    view_type: viewType,
    sort_by: null,
    sort_order: null,
    columns: null,
    is_org_preset: false,
    is_default: false,
  };

  if (viewType === "clients") {
    return [
      {
        ...basePreset,
        id: "platform:all-clients",
        name: "All Clients",
        filters: {
          status: "all",
          priority: "all",
          assignedTo: "all",
          lastContactRange: "all",
          search: "",
        } as TFilters,
        sort_by: "updated_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:active-clients",
        name: "Active Clients",
        filters: {
          status: "client",
          priority: "all",
          assignedTo: "all",
          lastContactRange: "all",
          search: "",
        } as TFilters,
        sort_by: "name",
        sort_order: "asc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:leads",
        name: "Leads",
        filters: {
          status: "lead",
          priority: "all",
          assignedTo: "all",
          lastContactRange: "all",
          search: "",
        } as TFilters,
        sort_by: "created_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:prospects",
        name: "Prospects",
        filters: {
          status: "prospect",
          priority: "all",
          assignedTo: "all",
          lastContactRange: "all",
          search: "",
        } as TFilters,
        sort_by: "updated_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:high-priority",
        name: "High Priority",
        filters: {
          status: "all",
          priority: "high",
          assignedTo: "all",
          lastContactRange: "all",
          search: "",
        } as TFilters,
        sort_by: "priority",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:urgent",
        name: "Urgent",
        filters: {
          status: "all",
          priority: "urgent",
          assignedTo: "all",
          lastContactRange: "all",
          search: "",
        } as TFilters,
        sort_by: "priority",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:dormant-30-days",
        name: "Dormant 30+ Days",
        filters: {
          status: "all",
          priority: "all",
          lastContactRange: "dormant-30",
          assignedTo: "all",
          search: "",
        } as TFilters,
        sort_by: "last_contact_date",
        sort_order: "asc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:high-value-accounts",
        name: "High-Value Accounts",
        filters: {
          status: "all",
          priority: "all",
          assignedTo: "all",
          lastContactRange: "all",
          search: "",
        } as TFilters,
        sort_by: "estimated_value",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:recent-contact",
        name: "Recent Contact",
        filters: {
          status: "all",
          priority: "all",
          lastContactRange: "last-30-days",
          assignedTo: "all",
          search: "",
        } as TFilters,
        sort_by: "last_contact_date",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ] as NormalizedSavedView<TFilters>[];
  }

  if (viewType === "projects") {
    return [
      {
        ...basePreset,
        id: "platform:all-projects",
        name: "All Projects",
        filters: {
          ownerId: "all",
          stage: "all",
          dateRange: "all",
          projectType: "all",
          tags: "",
          search: "",
        } as TFilters,
        sort_by: "updated_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:active-projects",
        name: "Active Projects",
        filters: {
          ownerId: "all",
          stage: "ACTIVE",
          dateRange: "all",
          projectType: "all",
          tags: "",
          search: "",
        } as TFilters,
        sort_by: "name",
        sort_order: "asc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:in-progress",
        name: "In Progress",
        filters: {
          ownerId: "all",
          stage: "IN_PROGRESS",
          dateRange: "all",
          projectType: "all",
          tags: "",
          search: "",
        } as TFilters,
        sort_by: "start_date",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:on-hold",
        name: "On Hold",
        filters: {
          ownerId: "all",
          stage: "ON_HOLD",
          dateRange: "all",
          projectType: "all",
          tags: "",
          search: "",
        } as TFilters,
        sort_by: "updated_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:completed",
        name: "Completed",
        filters: {
          ownerId: "all",
          stage: "COMPLETED",
          dateRange: "all",
          projectType: "all",
          tags: "",
          search: "",
        } as TFilters,
        sort_by: "end_date",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:draft",
        name: "Draft Projects",
        filters: {
          ownerId: "all",
          stage: "DRAFT",
          dateRange: "all",
          projectType: "all",
          tags: "",
          search: "",
        } as TFilters,
        sort_by: "created_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ] as NormalizedSavedView<TFilters>[];
  }

  if (viewType === "estimates") {
    return [
      {
        ...basePreset,
        id: "platform:all-estimates",
        name: "All Estimates",
        filters: { status: "all", search: "" } as TFilters,
        sort_by: "updated_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:pending",
        name: "Pending",
        filters: { status: "pending", search: "" } as TFilters,
        sort_by: "created_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:approved",
        name: "Approved",
        filters: { status: "approved", search: "" } as TFilters,
        sort_by: "updated_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:sent",
        name: "Sent",
        filters: { status: "sent", search: "" } as TFilters,
        sort_by: "sent_date",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        ...basePreset,
        id: "platform:expired",
        name: "Expired",
        filters: { status: "expired", search: "" } as TFilters,
        sort_by: "expires_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ] as NormalizedSavedView<TFilters>[];
  }

  if (viewType === "contacts") {
    return [
      {
        ...basePreset,
        id: "platform:all-contacts",
        name: "All Contacts",
        filters: { search: "" } as TFilters,
        sort_by: "updated_at",
        sort_order: "desc",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ] as NormalizedSavedView<TFilters>[];
  }

  return [];
}
